// Predicados SÍNCRONOS del signup (todo menos el email) como una sola schema Zod.
//
// Por qué Zod y no el email acá: el email es una cascada async richer (formato →
// TLD → coincidencia → typo → existencia/deliverability) que ya vive, pura y
// testeada, en `./emailValidation`. Zod es la fuente única del RESTO — nombre,
// apellido, password (policy + confirmación) y T&C — para que "la lista de
// condiciones" viva declarada en un lugar. El gate (`signup-machine`) compone
// ESTA schema + el veredicto de email + el check async en UN solo punto de
// decisión.

import { z } from 'zod';
import { isPasswordValid } from './password-policy';

// Claves de campo del form. Únicas para toda la feature (schema, máquina, page).
// Deben coincidir con los `name`/`ref` de los inputs en Signup.tsx.
export type FieldKey =
  | 'first_name'
  | 'last_name'
  | 'email'
  | 'email_confirm'
  | 'password'
  | 'terms';

// Mensajes co-localizados con la regla que los elige (mismos textos que hoy).
export const NAME_REQUIRED_MSG = 'Falta tu nombre';
export const LASTNAME_REQUIRED_MSG = 'Falta tu apellido';
export const PASSWORD_POLICY_MSG = 'Revisá la contraseña';
export const TERMS_REQUIRED_MSG =
  'Necesitás aceptar los Términos y Condiciones para continuar.';

// Entrada que parsea la schema (los campos no-email). El email se valida aparte.
export interface SignupSyncInput {
  first_name: string;
  last_name: string;
  password: string;
  terms: boolean;
}

// La schema. Todas las condiciones sync no-email, declarativas. `password` usa
// EXACTAMENTE la misma `isPasswordValid` que el checklist en vivo y el backend
// (fuente única en `./password-policy`), así que un usuario nunca pasa acá y es
// rechazado por Cognito. Ya no hay `password_confirm`: el botón de mostrar/ocultar
// (ojo) reemplaza el campo de repetir contraseña.
export const SignupDetailsSchema = z.object({
  first_name: z.string().trim().min(1, NAME_REQUIRED_MSG),
  last_name: z.string().trim().min(1, LASTNAME_REQUIRED_MSG),
  password: z.string().refine(isPasswordValid, { message: PASSWORD_POLICY_MSG }),
  terms: z.literal(true, {
    errorMap: () => ({ message: TERMS_REQUIRED_MSG }),
  }),
});

/**
 * Mapea los issues de Zod a errores por-campo (uno por campo, el primero gana).
 * Las claves de `path[0]` de la schema son FieldKeys por construcción.
 */
export function mapSchemaIssues(error: z.ZodError): Partial<Record<FieldKey, string>> {
  const out: Partial<Record<FieldKey, string>> = {};
  for (const issue of error.issues) {
    const key = issue.path[0];
    if (typeof key === 'string' && !(key in out)) {
      out[key as FieldKey] = issue.message;
    }
  }
  return out;
}
